import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import northstarLogo from '@/assets/brand/northstar-logo-blue.svg';
import happometerIcon from '@/assets/brand/Icon_icon_black.svg';

/**
 * NSLoader Component
 * A minimalist loader with a soft animated gradient background for NS Survey.
 * @param {boolean} isLoading - Controls the visibility of the loader overlay.
 */
export const NSLoader = ({ isLoading = true, fullScreen = true }: { isLoading?: boolean; fullScreen?: boolean }) => {
    return (
        <AnimatePresence>
            {isLoading && (
                <motion.div
                    key="ns-loader-overlay"
                    initial={{ opacity: 1 }}
                    exit={{
                        opacity: 0,
                        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                    }}
                    className={fullScreen
                        ? "fixed inset-0 left-0 top-0 z-[9999] flex size-full max-w-[100vw] items-center justify-center overflow-hidden bg-slate-100 font-sans"
                        : "relative flex size-full min-h-[400px] max-w-full items-center justify-center overflow-hidden bg-slate-100 font-sans"
                    }
                >
                    {/* Static backdrop — no motion so the whole overlay doesn’t “breathe” or shift */}
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                        <div
                            className="absolute inset-0"
                            style={{
                                background: "linear-gradient(148deg, #e9eef4 0%, #dbeafe 42%, #e2e8f6 100%)",
                            }}
                        />
                        <div className="absolute inset-0 bg-[radial-gradient(#475569_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.035]" />
                    </div>

                    {/* Fixed-width stack: all sizes in px to avoid root font-size branding shifts */}
                    <div className="relative z-10 flex w-[min(280px,92vw)] flex-col items-center [contain:layout_style]">
                        <div className="relative mb-[24px] box-content flex h-[116px] w-[116px] shrink-0 items-center justify-center">
                            <div className="pointer-events-none absolute inset-0 rounded-full border border-blue-600/22" />
                            <motion.div
                                className="pointer-events-none absolute -inset-[8px] rounded-full border-2 border-t-blue-600 border-r-transparent border-b-blue-200/45 border-l-transparent"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1.75, repeat: Infinity, ease: "linear" }}
                                style={{ willChange: "transform" }}
                            />
                            <div className="relative z-10 flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-[16px] border border-slate-100 bg-white p-[4px] shadow-md shadow-slate-200/70">
                                <img src={happometerIcon} alt="" className="size-full object-contain" />
                            </div>
                        </div>

                        <div className="flex w-full flex-col items-center gap-[16px] text-center">
                            <div className="flex h-[28px] w-full max-w-[200px] items-center justify-center">
                                <img
                                    src={northstarLogo}
                                    alt="Northstar"
                                    width={234}
                                    height={48}
                                    loading="eager"
                                    decoding="sync"
                                    fetchPriority="high"
                                    className="h-[28px] max-h-[28px] w-auto max-w-full object-contain object-center"
                                />
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
